import React, { useCallback, useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Button } from "@blueprintjs/core";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AppToaster } from "@components";
import { withToaster } from "@utils";

import { postIssueSentimentFeedback } from "./api";

const PageBodyWrapper = styled.div`
  padding-top: var(--global-spacing-navbar-height);
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--indigo-gradient-245);
  color: var(--global-light-gray);
  justify-content: flex-start;
  min-height: 100vh;
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.p`
  font-size: 36px;
  color: white;
  text-align: center;
  padding-top: 1em;
`;

const SubHeader = styled.header`
  font-size: var(--global-font-size-s);
  text-align: center;
  max-width: 440px;
`;

const Label = styled.label`
  margin-top: 2em;
  text-align: center;
  font-size: var(--global-font-size-s);
`;

const StyledButton = styled(Button)`
  margin: 7px;
`;

const CenteredTextArea = styled.textarea`
  color: white;
  font-size: 15px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top: 1em;
  margin-bottom: 1em;
  min-width: 523px;
  min-height: 165px;
  background: #2e0057;
  border: 1px solid rgba(255, 255, 255, 0.49);
  box-sizing: border-box;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 1px 1px rgba(16, 22, 26, 0.2);
  backdrop-filter: blur(5px);
  border-radius: 3px;
  ::placeholder {
    font-family: Inter;
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 16px;
    color: rgba(210, 210, 210, 0.8);
  }
  padding-left: 11px;
  padding-top: 11px;
`;

const TEXT_AREA_PLACEHOLDER =
  "False positive? Unhelpful message? Too noisy?...";

export const IssueSentimentResponseScreen: React.FC = () => {
  const [feedback, setFeedback] = useState("");
  const history = useHistory();
  const urlParams: URLSearchParams = new URLSearchParams(
    history.location.search
  );
  const deploymentId = urlParams.get("deploymentId") || "";
  const publicHash = urlParams.get("hash") || "";
  const sentiment = urlParams.get("sentiment") || "";
  const issueId = urlParams.get("issueId") || "";

  const changeFeedback = useCallback((event) => {
    setFeedback(event.target.value);
  }, []);

  const previousString = (): string => {
    const prev = document.referrer;
    let referrer: string;

    if (prev.includes("github")) {
      referrer = "GitHub";
    } else if (prev.includes("gitlab")) {
      referrer = "GitLab";
    } else {
      referrer = "previous page";
    }

    return `Back to ${referrer}`;
  };

  const goBack = useCallback(() => history.goBack(), [history]);

  const handleSubmit = (e: React.FormEvent<HTMLElement>) => {
    e.preventDefault();
    withToaster(
      () =>
        postIssueSentimentFeedback(
          feedback,
          deploymentId,
          publicHash,
          sentiment,
          issueId
        ),
      () => `Error posting feedback`
    )().then(() => {
      AppToaster.show({
        message: "Thank you for the feedback",
        intent: "success",
      });
    });
  };

  return (
    <>
      <Helmet>Thank you for your feedback!</Helmet>
      <PageBodyWrapper>
        <TextSection>
          <Header>Thanks for your feedback!</Header>
          <SubHeader>
            This will help improve findings for your team and the open source
            community.
          </SubHeader>
        </TextSection>
        <form>
          <TextSection>
            <Label>
              Additional feedback is welcome below:
              <CenteredTextArea
                placeholder={TEXT_AREA_PLACEHOLDER}
                value={feedback}
                onChange={changeFeedback}
              />
            </Label>
            <div>
              <StyledButton intent="primary" onClick={goBack}>
                <FontAwesomeIcon icon={faChevronLeft} />
                {previousString()}
              </StyledButton>
              <StyledButton
                icon="send-message"
                intent="success"
                onClick={handleSubmit}
              >
                Send comment
              </StyledButton>
            </div>
          </TextSection>
        </form>
      </PageBodyWrapper>
    </>
  );
};
